import React, { useState, useEffect, useRef } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { Team } from '@/types';
import { supabase } from '@/lib/supabaseClient';
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TestDataBanner } from "@/components/TestDataBanner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useUsers } from "@/components/settings/hooks/useUsers";
import { useTeams } from "@/components/settings/hooks/useTeams";
import { useInstallers } from "@/components/settings/hooks/useInstallers";
import { PipelinesTab } from "@/components/pipeline/PipelinesTab";
import { TemplatesTab } from "@/components/settings/TemplatesTab";
import { MilestonesTab } from "@/components/settings/MilestonesTab";
import { UsersManagementTab } from "@/components/settings/components/UsersManagementTab";
import { AutomationManagement } from "@/components/settings/components/AutomationManagement";
import { NotificationTemplates } from "@/components/NotificationTemplates";
import { LogsTab } from "@/components/settings/components/LogsTab";
import { KnowledgeBaseSettings } from "@/components/settings/components/KnowledgeBaseSettings";
import { ThemeTab } from "@/components/settings/ThemeTab";
import { EmailDomainConfiguration } from "@/components/settings/components/notifications/EmailDomainConfiguration";
import { EmailSignatureSettings } from "@/components/settings/components/notifications/EmailSignatureSettings";
import { TwilioConfiguration } from "@/components/settings/components/notifications/TwilioConfiguration";
import { DocumentReminderSettings } from "@/components/settings/components/notifications/DocumentReminderSettings";
import { AddersTab } from "@/components/settings/components/adders/AddersTab";
import { IntegrationsTab } from "@/components/settings/integrations/IntegrationsTab";
import { FeatureAccessNotice } from "@/components/settings/FeatureAccessNotice";
import { PERMISSION_DISPLAY } from "@/lib/permissionDisplay";
import { ContractSendingIdentitySettings } from "@/components/settings/contracts/ContractSendingIdentitySettings";
import { DesignSettings } from "@/components/settings/features/design/DesignSettings";
import { ContractStorageSweepCard } from "@/components/settings/features/contracts/ContractStorageSweepCard";
import { KnockModeSettings } from "@/components/settings/features/knock/KnockModeSettings";
import { AccountModulesSettings } from "@/components/settings/features/modules/AccountModulesSettings";
import { useOptimizedPermissions } from "@/hooks/useOptimizedPermissions";
import { useAccountFeatures } from "@/hooks/useAccountFeatures";
import { featureVisibleTo } from "@/lib/accountFeatures";
import { useLocation } from 'react-router-dom';

/** Mirrors the grouped Settings nav in the sidebar; keys are the ?tab= values. */
const SECTION_LABELS: Record<string, string> = {
  users: 'Users',
  pipelines: 'Pipelines',
  templates: 'Templates',
  notifications: 'Notifications',
  contracts: 'Contracts',
  design: 'Design',
  webhooks: 'Automation',
  adders: 'Adders',
  knock: 'Knock Mode',
  integrations: 'Integrations',
  // TBR-1.2 — label only. The `theme` key stays: it is the ?tab= value in every existing
  // bookmark and in validTabs below, and it matches the tenant_theme_settings vocabulary
  // the theme loader, proposals branding and customer portal all still speak.
  theme: 'Branding',
  logs: 'Logs',
  knowledge: 'AI',
};

const Settings: React.FC = () => {
  const { canManageSettings, hasAnyPermission, isSuperadmin } = useOptimizedPermissions();
  const { isOn: accountHasModule, loading: accountModulesLoading } = useAccountFeatures();
  const [activeTab, setActiveTab] = useState('users');
  const [isMounted, setIsMounted] = useState(false);
  const hasAccessedUsersTab = useRef(false);
  const location = useLocation();

  const { toast } = useToast();
  const isFetchingTeams = useRef(false);
  const isFetchingDeletedUsers = useRef(false);

  const {
    users = [],
    deletedUsers = [],
    loading: usersLoading = false,
    deletedUsersLoading = false,
    roles = [],
    managers = [],
    teams: userTeams = [],
    createUser,
    updateUser,
    deleteUser,
    fetchDeletedUsers,
    fetchUsers,
    fetchTeams: fetchUserTeams,
    addUserError,
  } = useUsers();

  const {
    teams: allTeams = [],
    isLoading: teamsLoading = false,
    fetchTeams,
    createTeam,
    updateTeam,
    deleteTeam: deleteTeamWithResult,
  } = useTeams();

  const {
    installers = [],
    isLoading: installersLoading = false,
    createInstaller,
    updateInstaller,
    deleteInstaller: deleteInstallerWithResult,
  } = useInstallers();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabFromUrl = searchParams.get('tab');
    const validTabs = ['users', 'pipelines', 'templates', 'notifications', 'contracts', 'design', 'webhooks', 'logs', 'theme', 'knowledge', 'adders', 'integrations', 'knock', 'modules'];
    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [location.search]);


  const handleRefreshData = async () => {
    try {
      const { data, error } = await supabase
        .from('stages')
        .select('*')
        .order('stage_order');
      if (error) {
        if (error.code === '42P01') {
          toast({
            title: "Error",
            description: "Stages table not found. Using default stages.",
            variant: "destructive",
          });
          return;
        }
        throw error;
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh stages.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      // Fetch teams if needed
      if (allTeams.length === 0 && !isFetchingTeams.current) {
        isFetchingTeams.current = true;
        fetchTeams().finally(() => {
          isFetchingTeams.current = false;
        });
      }
      // Fetch deleted users only once when first accessing users tab
      if (!hasAccessedUsersTab.current && !isFetchingDeletedUsers.current) {
        hasAccessedUsersTab.current = true;
        isFetchingDeletedUsers.current = true;
        fetchDeletedUsers().finally(() => {
          isFetchingDeletedUsers.current = false;
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, allTeams.length]);

  const handleCreateTeam = async (teamData: Omit<Team, 'id'>): Promise<void> => {
    await createTeam(teamData);
    // Also refresh the user teams to ensure they're in sync
    try {
      await fetchUserTeams();
    } catch (error) {
      console.warn('Failed to refresh user teams after team creation:', error);
    }
  };

  const handleDeleteTeam = async (teamId: string): Promise<void> => {
    await deleteTeamWithResult(teamId);
    // Also refresh the user teams to ensure they're in sync
    try {
      await fetchUserTeams();
    } catch (error) {
      console.warn('Failed to refresh user teams after team deletion:', error);
    }
  };

  const handleUpdateTeam = async (teamId: string, updates: Partial<Team>): Promise<void> => {
    // Find the existing team to merge with updates
    const existingTeam = allTeams.find(t => t.id === teamId);
    if (!existingTeam) {
      throw new Error('Team not found');
    }
    
    // Merge existing team data with updates
    const updatedTeam = { ...existingTeam, ...updates };
    await updateTeam(updatedTeam);
    // Also refresh the user teams to ensure they're in sync
    try {
      await fetchUserTeams();
    } catch (error) {
      console.warn('Failed to refresh user teams after team update:', error);
    }
  };

  const handleDeleteInstaller = async (installerId: string): Promise<void> => {
    await deleteInstallerWithResult(installerId);
  };

  const isLoading = usersLoading || teamsLoading || installersLoading;

  // ⚠️ THESE WERE HARDCODED `true`, AND THE SERVER NOW DISAGREES.
  //
  // Until 20260812u, `users_update` and `users_delete` carried no role predicate
  // at all — any authenticated user could delete or modify any user row in their
  // own tenant — so a blanket `true` here was, in effect, accurate. That was
  // F-19. The policies now require `manage_settings` or `user.manage_all`.
  //
  // Leaving these hardcoded would be worse than the original bug rather than
  // better: every user would still be OFFERED Edit and Delete, and every one
  // without the permission would get a refusal on click. A control that is
  // visible and broken teaches the user less than one that is absent.
  //
  // The pair matches `UsersTab.tsx`'s own gate and the policy predicate exactly,
  // so the screen and the database now answer the same question. If you change
  // one, change all three: here, UsersTab, and 20260812u.
  const canManageUsers = hasAnyPermission(['manage_settings', 'user.manage_all']);

  // Teams and installers are not governed by 20260812u — those tables have their
  // own policies and are untouched by F-19. They are gated on the same pair for
  // consistency of the Settings surface, NOT because a policy demands it; if
  // that turns out to hide something people legitimately do, loosen these two
  // without touching canManageUsers, which is the one backed by a policy.
  const canManageTeams = hasAnyPermission(['manage_settings', 'user.manage_all']);
  const canManageInstallers = hasAnyPermission(['manage_settings', 'user.manage_all']);
  // CB-8.1 review: the Contracts admin surface controls feature access + edits tenant email/templates,
  // so gate it on an actual admin/manage-settings permission (not the page's blanket `true` flags).
  // RBAC-6.1 / FR-19: and only where the account has Contracts at all. A tenant admin in an account with
  // it switched off does not see its settings, so a hand-typed ?tab=contracts shows nothing either.
  const canManageContractSettings =
    canManageSettings &&
    !accountModulesLoading &&
    featureVisibleTo({ accountEnabled: accountHasModule('contracts'), isSuperadmin });

  const ErrorFallback = ({ error }: FallbackProps) => (
    <div className="p-4 text-destructive">
      Error loading settings tab: {error instanceof Error ? error.message : String(error)}. Check component exports in settings/components/*.
    </div>
  );

  return (
    <div className="container mx-auto py-10">
      {/*
        The tab bar used to be what told you which section you were in. With it
        gone the sidebar carries the selection, but the page still needs to name
        itself — otherwise every settings screen is headed simply "Settings".
      */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-foreground">
          <span className="text-muted-foreground font-normal">Settings</span>
          <span className="text-muted-foreground font-normal mx-2">/</span>
          {SECTION_LABELS[activeTab] ?? 'Settings'}
        </h1>
      </div>

      <TestDataBanner className="mb-6" />

      <Tabs value={activeTab} className="space-y-4">

        <div className="flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <TabsContent value="users" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Users tab.</div>}>
                  <UsersManagementTab
                    // Users props
                    users={users}
                    deletedUsers={deletedUsers}
                    usersLoading={usersLoading}
                    deletedUsersLoading={deletedUsersLoading}
                    roles={roles}
                    teams={userTeams}
                    managers={managers}
                    createUser={createUser}
                    updateUser={updateUser}
                    deleteUser={deleteUser}
                    fetchDeletedUsers={() => {
                      // Reset the guard and fetch deleted users
                      isFetchingDeletedUsers.current = false;
                      fetchDeletedUsers();
                    }}
                    fetchUsers={fetchUsers}
                    addUserError={addUserError}
                    onEmailChange={async (email: string) => {
                      try {
                        const { data, error } = await supabase
                          .from('users')
                          .select('id')
                          .eq('email', email)
                          .maybeSingle();
                        if (data && data.id) {
                          return { exists: true };
                        }
                        if (error) {
                          throw new Error('Error checking email uniqueness');
                        }
                        return { exists: false };
                      } catch (error) {
                        console.error('Error checking email:', error);
                        return { exists: false };
                      }
                    }}
                    onEmailCheck={async (email: string) => {
                      try {
                        const { data, error } = await supabase
                          .from('users')
                          .select('id')
                          .eq('email', email)
                          .maybeSingle();
                        if (data && data.id) {
                          return true;
                        }
                        if (error) {
                          throw new Error('Error checking email uniqueness');
                        }
                        return false;
                      } catch (error) {
                        console.error('Error checking email:', error);
                        return false;
                      }
                    }}
                    currentUserId={undefined}
                    // Teams props
                    allTeams={allTeams}
                    teamsLoading={teamsLoading}
                    fetchTeams={fetchTeams}
                    createTeam={handleCreateTeam}
                    updateTeam={handleUpdateTeam}
                    deleteTeam={handleDeleteTeam}
                    // Installers props
                    installers={installers}
                    installersLoading={installersLoading}
                    createInstaller={createInstaller}
                    updateInstaller={updateInstaller}
                    deleteInstaller={handleDeleteInstaller}
                    // Permissions
                    canManageUsers={canManageUsers}
                    canManageTeams={canManageTeams}
                    canManageInstallers={canManageInstallers}
                  />
                </ErrorBoundary>
              </TabsContent>

              <TabsContent value="pipelines" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Pipelines tab.</div>}>
                  <PipelinesTab />
                </ErrorBoundary>
                {/* MST-1.1. Milestones sit under Pipelines rather than taking their own
                    nav entry: a milestone does nothing until a stage points at it, and
                    the stage editor is reached from the list above. */}
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Milestones.</div>}>
                  <MilestonesTab />
                </ErrorBoundary>
              </TabsContent>



              <TabsContent value="templates" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Templates tab.</div>}>
                  <TemplatesTab />
                </ErrorBoundary>
              </TabsContent>

              <TabsContent value="notifications" className="space-y-4">
                {/* Email Configuration - shown for all tenants except Solerro */}
                <EmailDomainConfiguration />

                {/* Email Signature Settings */}
                <EmailSignatureSettings />

                {/* SMS Configuration (Twilio) */}
                <TwilioConfiguration />

                {/* Document Expiry Reminders */}
                <DocumentReminderSettings />

                {/* Notification Templates */}
                <div>
                  <p className="text-muted-foreground mb-4">
                    Customize notification content and templates for different notification types.
                  </p>
                  <NotificationTemplates />
                </div>
              </TabsContent>

              {/*
                CB-8.1: this surface explains contract feature access and edits tenant
                email/templates, so it needs a real permission rather than the page's
                blanket `true` flags. That gate used to live on the tab trigger; with
                the tab bar gone it has to live on the panel, or removing the bar would
                have quietly unlocked it for anyone who typed the URL.
              */}
              {canManageContractSettings && (
                <TabsContent value="contracts" className="space-y-4">
                  <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Contracts tab.</div>}>
                    {/* RBAC-6.2: the role picker is gone; who uses Contracts is a permission now. */}
                    <FeatureAccessNotice feature="Contracts" permissionLabel={PERMISSION_DISPLAY.view_contracts.label} />
                    <ContractSendingIdentitySettings />
                    {/* COM-9.12. Gates itself on system.override and renders nothing
                        otherwise, the way the Design and Commission cards do: the
                        contracts bucket spans every account, so its sweeper is not a
                        tenant's fact. The database refuses the read too. */}
                    <ContractStorageSweepCard />
                  </ErrorBoundary>
                </TabsContent>
              )}

              {/* SD-8.1/SD-8.3. Deliberately NOT behind canManageSettings, unlike Contracts:
                  UX-DR17 requires a role without manage_settings to see the cards READ-ONLY with
                  the reason in words, so hiding the panel would replace a specified state with a
                  blank page. The component gates itself on useDesignFeatureAccess (FR-29) — a
                  tenant whose design gate is closed still renders nothing at all here. */}
              <TabsContent value="design" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Design tab.</div>}>
                  <DesignSettings />
                </ErrorBoundary>
              </TabsContent>

              {/* KNK-1.11 / FR-11. The component gates itself (useKnockMode + knockModeSettingsVisible)
                  and renders nothing for an account with Knock Mode off unless a superadmin is
                  looking, so a hand-typed ?tab=knock shows an empty page, not the feature. */}
              <TabsContent value="knock" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Knock Mode.</div>}>
                  <KnockModeSettings />
                </ErrorBoundary>
              </TabsContent>

              {/* KNK-1.55 / FR-11a. Gates itself on system.override and renders nothing otherwise;
                  every RPC behind it is superadmin-only in the database too. */}
              <TabsContent value="modules" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Add-on modules.</div>}>
                  <AccountModulesSettings />
                </ErrorBoundary>
              </TabsContent>

              <TabsContent value="webhooks" className="space-y-4">
                <AutomationManagement />
              </TabsContent>

              <TabsContent value="adders" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Adders tab.</div>}>
                  <AddersTab />
                </ErrorBoundary>
              </TabsContent>

              <TabsContent value="integrations" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Integrations tab.</div>}>
                  <IntegrationsTab />
                </ErrorBoundary>
              </TabsContent>

              <TabsContent value="theme" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Theme tab.</div>}>
                  <ThemeTab />
                </ErrorBoundary>
              </TabsContent>

              <TabsContent value="logs" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-destructive">Error loading Logs tab.</div>}>
                  <LogsTab />
                </ErrorBoundary>
              </TabsContent>

              <TabsContent value="knowledge" className="space-y-4">
                <ErrorBoundary FallbackComponent={() => <div className="p-4 text-red-500">Error loading AI tab.</div>}>
                  <KnowledgeBaseSettings />
                </ErrorBoundary>
              </TabsContent>
            </ErrorBoundary>
          )}
        </div>
      </Tabs>
      

    </div>
  );
};

export default Settings;